// dlms-frontend/src/pages/ReportsPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import withAuth from '../components/authHOC';
import { Button, DatePicker, Form, Select, Table, Tag } from 'antd';
import { useForm } from 'antd/es/form/Form';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { SearchOutlined } from '@ant-design/icons';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { LiaDownloadSolid } from 'react-icons/lia';
import { AUTH_URL } from '../constants';
import { notify, notify_success } from './LoginPage';
import { toastStyle } from '../components/Toaster';
import ExpectedPaymentsPDF, { ExpectedPaymentRecord } from '../components/commons/ExpectedPaymentsPDF';

interface Branch {
  _id: string;
  name: string;
}

interface InstallmentPeriod {
  _id: string;
  name: string;
}

const ReportsPage = () => {
  const [form] = useForm();

  // Filter state
  const [branches, setBranches] = useState<Branch[]>([]);
  const [installmentPeriods, setInstallmentPeriods] = useState<InstallmentPeriod[]>([]);
  const [metaLoading, setMetaLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  // Report state
  const [reportData, setReportData] = useState<ExpectedPaymentRecord[]>([]);
  const [summary, setSummary] = useState({ total: 0, paid: 0, notPaid: 0, date: '' });
  const [activeFilters, setActiveFilters] = useState({ branch: '', installmentPeriod: '', date: '' });
  const [hasResult, setHasResult] = useState(false);

  // Load branches and installment periods on mount
  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [branchRes, periodRes] = await Promise.allSettled([
          axios.get(`${AUTH_URL}/branches`, { withCredentials: true }),
          axios.get(`${AUTH_URL}/meta/installment-periods`, { withCredentials: true }),
        ]);

        if (branchRes.status === 'fulfilled') {
          const d = branchRes.value.data;
          const list = d.branches ?? d.data ?? d;
          setBranches(Array.isArray(list) ? list : []);
        }

        if (periodRes.status === 'fulfilled') {
          const d = periodRes.value.data;
          const list = d.installment_periods ?? d.installmentPeriods ?? d.data ?? d;
          setInstallmentPeriods(Array.isArray(list) ? list : []);
        }
      } finally {
        setMetaLoading(false);
      }
    };

    loadMeta();
  }, []);

  const fetchReport = async (values: any) => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (values.date) params.date = (values.date as Dayjs).format('YYYY-MM-DD');
      if (values.branch) params.branch = values.branch;
      if (values.installment_period) params.installment_period = values.installment_period;

      const response = await axios.get(`${AUTH_URL}/expected-payments`, {
        params,
        withCredentials: true,
      });

      if (response.data.success) {
        setReportData(Array.isArray(response.data.data) ? response.data.data : []);
        setSummary({
          total: response.data.total,
          paid: response.data.paid,
          notPaid: response.data.notPaid,
          date: response.data.date,
        });
        setActiveFilters({
          branch: branches.find(b => b._id === values.branch)?.name || '',
          installmentPeriod: installmentPeriods.find(p => p._id === values.installment_period)?.name || '',
          date: params.date || new Date().toISOString().split('T')[0],
        });
        setHasResult(true);
        if (response.data.total === 0) notify('No expected payments found for the selected filters.', toastStyle);
      } else {
        notify(response.data.message, toastStyle);
      }
    } catch (err: any) {
      notify(err.message, toastStyle);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '#',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 150,
    },
    {
      title: 'Phone',
      dataIndex: 'clientPhone',
      key: 'clientPhone',
      width: 100,
    },
    {
      title: 'Branch',
      dataIndex: 'branch',
      key: 'branch',
      width: 120,
    },
    {
      title: 'Borrowed',
      dataIndex: 'amountBorrowed',
      key: 'amountBorrowed',
      width: 110,
      align: 'right' as const,
      render: (v: number) => v?.toLocaleString(),
    },
    {
      title: 'Paid',
      dataIndex: 'amountPaid',
      key: 'amountPaid',
      width: 110,
      align: 'right' as const,
      render: (v: number) => v?.toLocaleString(),
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      width: 110,
      align: 'right' as const,
      render: (v: number) => v?.toLocaleString(),
    },
    {
      title: 'Last Payment',
      dataIndex: 'lastPaymentDate',
      key: 'lastPaymentDate',
      width: 120,
      render: (v: string | null) =>
        v ? new Date(v).toLocaleDateString('en-GB') : <span className="text-muted">—</span>,
    },
    {
      title: 'Status',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 100,
      align: 'center' as const,
      render: (v: string) =>
        v === 'PAID' ? (
          <Tag color="success">PAID</Tag>
        ) : (
          <Tag color="error">NOT PAID</Tag>
        ),
    },
  ];

  const dateLabel = activeFilters.date
    ? new Date(activeFilters.date).toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  // Sort: PAID first, then NOT PAID; within each group alphabetically
  const sortedData = useMemo(
    () =>
      [...(Array.isArray(reportData) ? reportData : [])].sort((a, b) => {
        if (a.paymentStatus === b.paymentStatus) return a.clientName.localeCompare(b.clientName);
        return a.paymentStatus === 'PAID' ? -1 : 1;
      }),
    [reportData]
  );

  // Memoize the PDF document so it only regenerates when data changes, not on every render
  const pdfDocument = useMemo(
    () => (
      <ExpectedPaymentsPDF
        data={sortedData}
        date={activeFilters.date}
        branch={activeFilters.branch}
        installmentPeriod={activeFilters.installmentPeriod}
        paid={summary.paid}
        notPaid={summary.notPaid}
      />
    ),
    [sortedData, activeFilters, summary]
  );

  return (
    <Container fluid id="users-container">
      {/* Filter bar */}
      <Row className="mb-3">
        <Col>
          <Form
            form={form}
            layout="horizontal"
            onFinish={fetchReport}
            initialValues={{ date: dayjs() }}
          >
            <Row className="d-flex align-items-end border py-2">
              <Col lg={3} md={6}>
                <Form.Item name="branch" label="Branch" className="my-0">
                  <Select
                    placeholder="All Branches"
                    allowClear
                    options={(branches ?? []).map(b => ({ value: b._id, label: b.name }))}
                  />
                </Form.Item>
              </Col>

              <Col lg={3} md={6}>
                <Form.Item name="installment_period" label="Installment Period" className="my-0">
                  <Select
                    placeholder="All Periods"
                    allowClear
                    options={(installmentPeriods ?? []).map(p => ({ value: p._id, label: p.name }))}
                  />
                </Form.Item>
              </Col>

              <Col lg={3} md={6}>
                <Form.Item name="date" label="Expected Payment Date" className="my-0">
                  <DatePicker className="w-100" format="DD/MM/YYYY" allowClear={false} />
                </Form.Item>
              </Col>

              <Col lg={2} md={6}>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="rounded-0 text-uppercase w-100"
                  loading={loading}
                  icon={<SearchOutlined />}
                >
                  {loading ? 'Loading...' : 'Get Report'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      {/* Results */}
      {hasResult && (
        <>
          {/* Summary cards */}
          <Row className="mb-3 g-2">
            {[
              { label: 'Total Expected', value: summary.total, color: '#1d3557' },
              { label: 'Paid', value: summary.paid, color: '#155724' },
              { label: 'Not Paid', value: summary.notPaid, color: '#721c24' },
            ].map(card => (
              <Col key={card.label} xs={6} md={3} lg={2}>
                <div
                  className="p-3 text-white"
                  style={{ backgroundColor: card.color, lineHeight: 1.2 }}
                >
                  <div style={{ fontSize: 11, opacity: 0.8, textTransform: 'uppercase' }}>
                    {card.label}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{card.value}</div>
                </div>
              </Col>
            ))}
          </Row>

          {/* Table */}
          <Row>
            <Col>
              <Table
                dataSource={sortedData}
                columns={columns}
                rowKey={(r: any) => r.loanId}
                pagination={{ pageSize: 50, showSizeChanger: true }}
                bordered
                className="rounded-0"
                tableLayout="fixed"
                style={{ overflow: 'auto' }}
                rowClassName={(record: any) =>
                  record.paymentStatus === 'NOT PAID' ? 'table-row-unpaid' : ''
                }
                title={() => (
                  <Row className="d-flex justify-content-between align-items-center">
                    <Col>
                      <span className="fw-bold text-uppercase">
                        Expected Payments — {dateLabel}
                        {activeFilters.branch ? ` | ${activeFilters.branch}` : ''}
                        {activeFilters.installmentPeriod
                          ? ` | ${activeFilters.installmentPeriod}`
                          : ''}
                      </span>
                    </Col>
                    <Col className="d-flex justify-content-end">
                      <PDFDownloadLink
                        document={pdfDocument}
                        fileName={`expected-payments-${activeFilters.date}.pdf`}
                      >
                        <Button className="rounded-0 text-uppercase">
                          <LiaDownloadSolid /> Download PDF
                        </Button>
                      </PDFDownloadLink>
                    </Col>
                  </Row>
                )}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default withAuth(ReportsPage, ['admin']);
